/**
 * Custom Hook for HomePage Data Management
 * Handles all data fetching and state management for the homepage
 */

import { useState, useEffect } from "react";
import { fetchAllHomePageData } from "../dataService";
import logger from "../../../logger";

/**
 * Initial state for page data
 */
const INITIAL_PAGE_DATA = {
  approveLines: 0,
  obligoExceptions: 0,
};

/**
 * Initial state for loading indicators
 */
const INITIAL_LOADING_STATE = {
  approveLines: true,
  obligoExceptions: true,
  obligoDetails: true,
};

/**
 * Hook to manage homepage data fetching and state
 * @param {Function} execute - MSAL fetch execution function
 * @param {Object} user - Current user object
 * @returns {Object} Page data, loading states, and obligo details
 */
export const useHomePageData = (execute, user) => {
  const [pageData, setPageData] = useState(INITIAL_PAGE_DATA);
  const [loading, setLoading] = useState(INITIAL_LOADING_STATE);
  const [obligoDetails, setObligoDetails] = useState([]);
  const [ordersLog, setOrdersLog] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userBranchesList || !user?.accessRights) {
        return;
      }

      try {
        const results = await fetchAllHomePageData(execute, user.accessRights);

        // Update approve lines data
        if (!results.approveLines.error) {
          setPageData((prev) => ({
            ...prev,
            approveLines: results.approveLines.count,
          }));
        }
        setLoading((prev) => ({ ...prev, approveLines: false }));

        // Update obligo data
        if (!results.obligoExceptions.error) {
          setPageData((prev) => ({
            ...prev,
            obligoExceptions: results.obligoExceptions.count,
          }));
          setObligoDetails(results.obligoExceptions.details);
        }
        setLoading((prev) => ({ ...prev, obligoExceptions: false }));

        // Update orders log
        if (!results.ordersLog.error) {
          setOrdersLog(results.ordersLog.data);
        }
      } catch (error) {
        logger.error("Failed to fetch homepage data", {
          error: error.message,
        });
      }
    };

    fetchData();
  }, [execute, user?.userBranchesList, user?.accessRights]);

  return {
    pageData,
    loading,
    obligoDetails,
    ordersLog,
  };
};
